import React, { useContext, useCallback, useRef, useEffect } from 'react';
import {
  bool, shape, string, number
} from 'prop-types';
import { ExperienceContext, useDomPath, useStore } from '@thd-nucleus/experience-context';
import { Row, Col } from '@thd-olt-component-react/grid';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { Fulfillment } from '@thd-olt-component-react/fulfillment';
import { ProductAddOns } from '@thd-olt-component-react/product-add-ons';
import { SubscribeAndSave } from '@thd-olt-component-react/subscribe-and-save';
import {
  extend,
  useDataModel
} from '@thd-nucleus/data-sources';
import { InstantCheckout } from '@thd-olt-component-react/instant-checkout';
import { BuyboxError } from './subcomponents/BuyboxError';
import { BuyboxUnavaiable } from './subcomponents/BuyboxUnavaiable';
import { BuyboxCalculator } from './subcomponents/BuyboxCalculator';
import { BuyboxLite } from './subcomponents/BuyboxLite';
import { BuyboxActions } from './subcomponents/BuyboxActions';
import { BuyboxLoader } from './subcomponents/BuyboxLoader';
import { useBuyBox } from './hooks/useBuyBox';
import * as helper from '../BuyboxHelper';
import * as calculatorHelper from './BuyboxCalcHelper';
import { track } from '../track';
import { dataModel } from './dataModel';
import './buybox.scss';

/**
 * Buybox container
 */
const Buybox = (props) => {
  const {
    channel,
    isServer
  } = useContext(ExperienceContext);

  const buyboxTimerRef = useRef(new Date());

  const {
    itemId,
    stash,
    hideQuantity,
    disableATC: buyboxDisableATC,
    lite,
    sticky,
    internally,
    lmUseContextForProps,
    hideInstantCheckout,
    hidePaypalCheckout
  } = props;

  const {
    bodfsMinimumThreshold,
    bopisMinimumThreshold,
    bopisMinimumThresholdStores,
    bopisHolidayTiming,
    covidAlert,
  } = useLayoutGroupProps({ props });

  const { storeId, storeZip } = useStore();
  const [buyboxState, setBuyboxState] = useBuyBox({ storeZip });

  const atcStash = helper.useATCLocationDDOStash({ stash, itemId });

  const productOptions = {
    skip: storeId === '8119',
    ssr: false,
    variables: {
      itemId
    }
  };

  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);

  useEffect(() => {
    if (data?.product) {
      if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
        const start = buyboxTimerRef.current.getTime();
        const end = new Date().getTime();
        LIFE_CYCLE_EVENT_BUS.trigger('buybox.loaded', { start, end });
      }
    }
  }, [data?.product]);

  const {
    fulfillment = {},
    info = {},
    availabilityType = {},
    pricing = {},
    installServices = {},
    specificationGroup = []
  } = data?.product || {};
  const price = pricing?.value || '';

  const onQuantityChange = useCallback(({ quantity }) => {
    const qty = parseInt(quantity, 10);
    if (!Number.isNaN(qty) && qty > 0) {
      setBuyboxState((prevState) => {
        return {
          ...prevState,
          quantity: qty,
          showTooltip: true
        };
      });
    }
  }, [buyboxState]);

  const onFulfillmentChange = ({
    currentFulfillment,
    method,
    isShowATC,
    disableATC,
    isOutOfStockOnline,
    quantityLimitExceed,
    availableFulfillments
  }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        fulfillmentMethod: method,
        availableFulfillments,
        isShowATC,
        disableATC,
        isOutOfStockOnline,
        currentFulfillment,
        quantityLimitExceed,
        // Reset Subscribe & save on fulfillment change
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: null
          }
        }
      };
    });
  };

  const onProductAddOnChange = (addOnData, productAddOnData) => {
    const { addOn, flags } = helper.getSelectedAddOnInfo(addOnData, buyboxState.fulfillmentMethod);
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        ...flags,
        productAddOns: {
          ...prevState.productAddOns,
          ...addOn
        }
      };
    });
  };

  const onSubscribeAndSaveChange = ({ frequency, isOptIn }) => {
    // isOptIn will be true if they have selected to subscribe
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: isOptIn ? frequency : null,
          }
        }
      };
    });
  };

  const onChangeCalculator = ({ quantity, coverage, error: errorCalc }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity,
        coverage,
        showTooltip: !errorCalc
      };
    });
  };

  const updateAdditionalQuantity = ({ addAdditional, size }) => {
    const { coverage, quantity } = buyboxState;
    let updatedQty = quantity;
    if (coverage) {
      const needed = addAdditional ? +coverage + (+coverage * 0.10) : coverage;
      updatedQty = Math.ceil(needed / size);
    } else if (addAdditional) {
      updatedQty = Math.ceil(+updatedQty + (+updatedQty * 0.10));
    }
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity: updatedQty || quantity,
        addAdditional
      };
    });
  };
  // ######## End of Calculator methods ##########
  const onDeliveryOptionsZipCodeChange = useCallback(({ zipCode }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        zipCode
      };
    });
  }, []);

  // call back on add to cart click
  const onATCClick = useCallback(() => {
    if (window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('@@ADD-TO-CART/LOCATION', atcStash);
    }
  }, []);

  const unavailable = buyboxDisableATC; // From prop
  const { hasSubscription, minimumOrderQuantity } = info || {};
  const {
    isOutOfStockOnline,
    isShowATC,
    quantity,
    quantityLimitExceed,
    showTooltip,
    addAdditional,
    zipCode,
    fulfillmentMethod,
    paypalEligible,
    productAddOns,
    availableFulfillments,
    assembleItemAtStore
  } = buyboxState;
  const isATCDisabled = unavailable || buyboxState?.disableATC;

  const { dualPath = false } = productAddOns;
  const orderQuantity = minimumOrderQuantity > quantity ? minimumOrderQuantity : quantity;

  const [pathForAdvisoryMessage, refForAdvisoryMessage] = useDomPath();

  // Do not load Buybox in the server side.
  if (isServer) return null;

  if (loading) {
    return (
      <BuyboxLoader />
    );
  }

  if (!data?.product) {
    return null;
  }

  if ((error && !data)) {
    return (
      <BuyboxError />
    );
  }
  if (unavailable) {
    return (
      <BuyboxUnavaiable
        hideQuantity={hideQuantity}
        itemId={itemId}
        quantity={orderQuantity}
        quantityLimitExceed={quantityLimitExceed}
        onQuantityChange={onQuantityChange}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
      />
    );
  }
  if (lite) {
    return (
      <BuyboxLite
        itemId={itemId}
        unavailable={unavailable}
        covidAlert={covidAlert}
        storeId={storeId}
        isOutOfStockOnline={isOutOfStockOnline}
        isShowATC={isShowATC}
        storeZip={storeZip}
        isATCDisabled={isATCDisabled}
        channel={channel}
        zipCode={zipCode}
        fulfillmentMethod={fulfillmentMethod}
        fulfillment={fulfillment}
        paypalEligible={paypalEligible}
        availabilityType={availabilityType}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        onATCClick={onATCClick}
        internally={internally}
        bopisHolidayTiming={bopisHolidayTiming}
        lmUseContextForProps={lmUseContextForProps}
        hideInstantCheckout={hideInstantCheckout}
        hidePaypalCheckout={hidePaypalCheckout}
      />
    );
  }
  const inventoryWarningMessage = !isOutOfStockOnline
    && isShowATC
    && !!minimumOrderQuantity
    && `We can only ship quantities of ${minimumOrderQuantity} or more`;

  return (
    <Row>
      <Col fallback="12" className="buybox-wrapper">
        <div className="buybox-top__wrapper">
          {!unavailable && (
            <div className="buybox__fulfillment-wrapper">
              <div className="buybox__fulfillment">
                <Fulfillment
                  itemId={itemId}
                  covidAlert={covidAlert}
                  quantity={orderQuantity}
                  onChange={onFulfillmentChange}
                  onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                  dualPath={dualPath}
                  bodfsMinimumThreshold={bodfsMinimumThreshold}
                  bopisMinimumThreshold={bopisMinimumThreshold}
                  bopisMinimumThresholdStores={bopisMinimumThresholdStores}
                  internally={internally}
                  bopisHolidayTiming={bopisHolidayTiming}
                />
              </div>
            </div>
          )}
        </div>
        {!isOutOfStockOnline && calculatorHelper.shouldDisplayCalculator({ pricing, info }) && !dualPath && (
          <BuyboxCalculator
            showTooltip={showTooltip}
            addAdditional={addAdditional}
            channel={channel}
            quantity={orderQuantity}
            pricing={pricing}
            info={info}
            fulfillment={fulfillment}
            installServices={installServices}
            specificationGroup={specificationGroup}
            onChangeCalculator={onChangeCalculator}
            updateAdditionalQuantity={updateAdditionalQuantity}
          />
        )}
        {!isOutOfStockOnline && isShowATC && (
          <>
            <ProductAddOns
              itemId={itemId}
              fulfillment={buyboxState.fulfillmentMethod}
              onChange={onProductAddOnChange}
              availableFulfillments={availableFulfillments}
            />
            {hasSubscription && (
              <SubscribeAndSave
                itemId={itemId}
                quantity={orderQuantity}
                fulfillment={buyboxState.fulfillmentMethod}
                onChange={onSubscribeAndSaveChange}
              />
            )}
          </>
        )}
        {inventoryWarningMessage && (
          <>
            {pathForAdvisoryMessage.length && track('advisory-message', {
              message: inventoryWarningMessage,
              path: pathForAdvisoryMessage
            })}
            <div className="alert-inline alert-inline--warning inventory--warning" ref={refForAdvisoryMessage}>
              <span>{inventoryWarningMessage}</span>
            </div>
          </>
        )}
        {!isOutOfStockOnline && isShowATC && !dualPath && (
          <BuyboxActions
            alterBrowserHistory
            disableATC={isATCDisabled}
            fulfillment={fulfillment}
            fulfillmentMethod={fulfillmentMethod}
            hideQuantity={hideQuantity}
            isAssembleItemAtStore={assembleItemAtStore}
            itemId={itemId}
            minimumOrderQuantity={minimumOrderQuantity}
            onATCClick={onATCClick}
            onQuantityChange={onQuantityChange}
            paypalEligible={paypalEligible}
            productAddOns={productAddOns}
            quantity={orderQuantity}
            quantityLimitExceed={quantityLimitExceed}
            sticky={sticky}
            storeId={storeId}
            storeZip={storeZip}
            zipCode={zipCode}
          />
        )}
      </Col>
    </Row>
  );
};

Buybox.displayName = 'Buybox';
Buybox.propTypes = {
  /**
   * The number of clicks you are allowed before seeing a warning.
   */
  itemId: string,
  /** will hide the buybox quantity box */
  hideQuantity: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  covidAlert: bool,
  /** Pass ddo values to override the default */
  disableATC: bool,
  lite: bool,
  stash: shape({
    pageType: string,
    sharedSection: string,
    section: string,
    component: string
  }),
  sticky: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bodfsMinimumThreshold: number,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisMinimumThreshold: number,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisMinimumThresholdStores: string,
  internally: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisHolidayTiming: number,
  lmUseContextForProps: bool,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool
};

Buybox.defaultProps = {
  itemId: null,
  lite: false,
  hideQuantity: false,
  disableATC: false,
  stash: {},
  covidAlert: false,
  sticky: false,
  bodfsMinimumThreshold: null,
  bopisMinimumThreshold: null,
  bopisMinimumThresholdStores: null,
  internally: false,
  bopisHolidayTiming: null,
  lmUseContextForProps: false,
  hideInstantCheckout: false,
  hidePaypalCheckout: false
};

Buybox.dataModel = extend(
  dataModel,
  Fulfillment,
  ProductAddOns,
  SubscribeAndSave,
  InstantCheckout
);

export default Buybox;
